import React, { createContext, useEffect, useState } from 'react';
import { getLoggedUser, getUserByUsername } from '../utils/users-hooks';
import i18n from '../i18n';
import { Role, WEB_ALLOWED_ROLES } from '../utils/Enums/Roles';
import { LoggedUser, UserGroup } from '../models/user-models';
import {
    clearStoredSession,
    getStoredRoles,
    isImpersonatedToken,
    storeRoles,
    storeSession,
} from '../utils/auth-storage';

interface AuthContextData {
    loggedUser: LoggedUser | null;
    token: string | null;
    logIn(email: string, password: string): Promise<number>;
    logOut(): void;
    setToken: React.Dispatch<React.SetStateAction<string | null>>;
    permissions: string[];
    setPermissions: React.Dispatch<React.SetStateAction<string[]>>;
    hasPermission(per: Role[]): boolean;
    group: UserGroup;
    mainPage: string;
    loading: boolean;
    isImpersonating: boolean;
    impersonateUser(targetUserId: number): Promise<number>;
    stopImpersonation(): Promise<number>;
}

export const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider: React.FC = ({ children }) => {
    const [token, setToken] = useState(localStorage.getItem('token'));
    const username = localStorage.getItem('username');
    const [permissions, setPermissions] = useState<string[]>(() => getStoredRoles());
    const [loggedUser, setLoggedUser] = useState<LoggedUser | null>(null);
    const [group, setGroup] = useState({} as UserGroup);
    const [navigatePage, setNavigatePage] = useState('');
    const [loading, setLoading] = useState<boolean>(() => Boolean(localStorage.getItem('token') && localStorage.getItem('username')));
    const isImpersonating = isImpersonatedToken(token);

    const getUserData = async (username?: string) => {
        try {
            const user = username ? await getUserByUsername(username) : await getLoggedUser();
            setLoggedUser(user);
            if (user.roles) {
                setPermissions(user.roles);
                storeRoles(user.roles);
            }
            if (user.group) {
                setGroup(user.group);
                await i18n.changeLanguage(user.group.language ? user.group.language.toLowerCase() : 'en');
            } else {
                setGroup({} as UserGroup);
            }

            const hasWebAccess = user.roles && user.roles.some((r) => WEB_ALLOWED_ROLES.includes(r as any));
            if (!hasWebAccess) {
                setNavigatePage('/no-access');
                return user;
            }

            for (let i = 0; i < user.roles.length; i++) {
                if (user.roles[i] === Role.ADMIN || user.roles[i] === Role.COORDINATOR || user.roles[i] === Role.DISTRIBUTOR || user.roles[i] === Role.INSTALLER) {
                    setNavigatePage('/dashboard');
                    break;
                } else if (user.roles[i] === Role.WAREHOUSE) {
                    setNavigatePage('/warehouses');
                    break;
                } else if (user.roles[i] === Role.FINANCIAL) {
                    setNavigatePage('/invoices');
                    break;
                }
            }
            return user;
        } catch (error) {
            console.error('Failed to load user data:', error);
            setLoggedUser(null);
            return null;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (username && token) {
            getUserData(username);
        } else {
            setLoading(false);
        }
    }, []);

    const logIn = async (email: string, password: string) => {
        const data = {
            username: email.trim(),
            password: password,
        };

        try {
            const userResponse = await fetch(`${process.env.REACT_APP_API_URL}login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });
            const accessToken = await userResponse.headers.get('Authorization');
            const refreshToken = await userResponse.headers.get('Refresh-Token');

            if (accessToken) {
                setToken(accessToken);
                storeSession(accessToken, refreshToken, data.username);
                setLoading(true);
                await getUserData(data.username);
                return userResponse.status;
            } else {
                clearUserData();
                return userResponse.status;
            }
        } catch (e) {
            clearUserData();
            return 500;
        }
    };

    const updateSessionFromResponse = async (response: Response) => {
        const accessToken = response.headers.get('Authorization');
        const refreshToken = response.headers.get('Refresh-Token');

        if (!accessToken || !response.ok) return false;

        const storedUsername = localStorage.getItem('username');
        if (!storedUsername) return false;

        storeSession(accessToken, refreshToken, storedUsername);
        setLoggedUser(null);
        setNavigatePage('');
        setToken(accessToken);
        setLoading(true);
        const user = await getUserData();
        if (user) {
            storeSession(accessToken, refreshToken, user.username, user.roles);
        }
        return Boolean(user);
    };

    const impersonateUser = async (targetUserId: number) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}impersonate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ targetUserId, clientType: 'WEB' }),
            });

            const sessionUpdated = await updateSessionFromResponse(response);
            return sessionUpdated ? response.status : 500;
        } catch (e) {
            return 500;
        }
    };

    const stopImpersonation = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}stop-impersonation`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            const sessionUpdated = await updateSessionFromResponse(response);
            return sessionUpdated ? response.status : 500;
        } catch (e) {
            return 500;
        }
    };

    const clearUserData = () => {
        setToken(null);
        setPermissions([]);
        setLoggedUser(null);
        setLoading(false);
        clearStoredSession();
    };

    const logOut = () => {
        fetch(`${process.env.REACT_APP_API_URL}logout`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
        }).catch(() => {});
        clearUserData();
        setNavigatePage('');
    };

    const hasPermission = (per: Role[]) => {
        return per.some((value) => permissions.includes(value));
    };

    return (
        <AuthContext.Provider
            value={{
                loggedUser,
                token,
                logIn,
                logOut,
                setToken,
                permissions,
                setPermissions,
                hasPermission,
                group,
                mainPage: navigatePage,
                loading,
                isImpersonating,
                impersonateUser,
                stopImpersonation,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
